import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Observation } from "@medplum/fhirtypes";

import { getPostpartumRows } from "@/encounters/components/EncounterLabor/services/postpartum";
import { injectBirthTimeObservation } from "@/encounters/components/EncounterLabor/utils/labor";
import { selectBirthDateTime, selectCurrentBirth } from "@/store/slices/birth";
import { selectPostpartumObservations } from "@/store/slices/postpartum-observations";

export interface PostpartumRow {
  time: string;
  observations: Observation[];
}

export function usePostpartumRows(flowsheetForms: Record<string, string>) {
  const [rows, setRows] = useState<[string, PostpartumRow[]][]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const observations = useSelector(selectPostpartumObservations);
  const birthDateTime = useSelector(selectBirthDateTime);
  const birthTimeObservation = useSelector(selectCurrentBirth);

  // Memoize the observations array to prevent unnecessary re-processing
  const observationsArray = useMemo(() => Object.values(observations), [observations]);

  const processRows = useCallback(async () => {
    if (observationsArray.length === 0 && !birthTimeObservation) {
      setRows([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const processedRows = await getPostpartumRows(flowsheetForms, observationsArray);

      // Inject birth time observation if we have one
      if (birthTimeObservation && birthDateTime) {
        injectBirthTimeObservation(processedRows, birthTimeObservation, birthDateTime);
      }

      setRows(Object.entries(processedRows));
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to process rows"));
      console.error("Error processing postpartum rows:", err);
    } finally {
      setIsLoading(false);
    }
  }, [flowsheetForms, observationsArray, birthTimeObservation, birthDateTime]);

  // Process rows whenever observations change
  useEffect(() => {
    processRows();
  }, [processRows]);

  // Manual refresh function
  const refresh = useCallback(() => {
    processRows();
  }, [processRows]);

  return {
    rows,
    error,
    isLoading,
    refresh,
  };
}
